import React from 'react';
import PropTypes from 'prop-types';
import { FormCheckbox, FormHelperText, FormRadio, FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import {
    emailSignOffMethodKey,
    onScreenSignOffMethodKey,
    resolveEmployeeSignOffMethodKey
} from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { SignatureField } from 'pages/employee-reviews/shared';

const defaultObject = {};
const defaultArray = [];

// Frontend copy describing each employee sign-off method, keyed by the API's method
// key - matches the descriptions the review-type editor's sign-off step already uses
// (ReviewTypeSignOffStep.jsx / reviewTypeEditorHelpers.js).
const signOffMethodDescriptions = {
    PHYSICAL: "Print and sign a PDF copy",
    ONSCREEN: "Employee enters their signature on screen during the review",
    EMAIL: "Email a temporary link to the driver for electronic sign-off",
    APP: "Employee can view and sign off the review via the app"
};

// Frontend copy explaining why the API reported a sign-off method as unavailable for this
// employee, keyed by the reason key it returns alongside the availability flag.
const unavailableSignOffReasons = {
    NOTDRIVERAPPUSER: "This employee is not registered as a driver app user."
};

// Covers a reason key this page does not recognise, so a disabled option is never left
// without an explanation of why it cannot be chosen.
const genericUnavailableSignOffReason = "This sign-off method is not available for this employee.";

// The Sign-off step of the complete-review wizard: the employee sign-off method
// (only the methods the review type offers are shown), whatever that method needs from the
// employee - their signature on screen, or the address the sign-off link is sent to - the
// manager's own signature when the review type requires one, and the confirmation checkbox.
export function SignOffStep(
    {
        signOff = defaultObject,
        employeeSignOffMethodId = null,
        employeeSignature = null,
        employeeSignedAt = null,
        signOffEmailAddress = "",
        signOffEmailValidationMessage = null,
        managerSignature = null,
        managerSignedAt = null,
        confirmChecked = false,
        loading = false,
        saving = false,
        onSignOffMethodChange,
        onEmployeeSignatureChange,
        onSignOffEmailAddressChange,
        onManagerSignatureChange,
        onConfirmChange
    }
) {

    const employeeSignOffMethodKey = resolveEmployeeSignOffMethodKey({ signOff }, employeeSignOffMethodId);

    function buildMethodOptions() {
        return (signOff?.employeeMethods ?? defaultArray).map((method) => {
            // The API reports a method the employee cannot use with an availability flag and a
            // reason key. The flag is compared against false so a response that omits it leaves
            // the method selectable rather than disabling every option.
            const unavailable = method.available === false;
            let tooltip = null;

            if (unavailable) {
                tooltip = unavailableSignOffReasons[method.unavailableReasonKey] ?? genericUnavailableSignOffReason;
            }

            return {
                value: method.id,
                label: method.name,
                helperText: signOffMethodDescriptions[method.key],
                disabled: unavailable,
                tooltip
            };
        });
    }

    function onMethodChange(event) {
        onSignOffMethodChange(Number(event.target.value));
    }

    function onConfirmCheckedChange(event) {
        onConfirmChange(event.target.checked);
    }

    function onEmailAddressChange(event) {
        onSignOffEmailAddressChange(event.target.value);
    }

    return (
        <div className="form-group">
            <h2>Sign-off</h2>
            <FormHelperText loading={loading}>Confirm and sign off this review.</FormHelperText>
            <FormRadio
                id="employee-sign-off-method"
                headingText="Employee Sign off method"
                loading={loading}
                disabled={saving}
                options={buildMethodOptions()}
                selectedValue={employeeSignOffMethodId}
                onChange={onMethodChange}
            />
            <ShowHide
                evaluator={employeeSignOffMethodKey === onScreenSignOffMethodKey}
                show={(
                    <SignatureField
                        id="employee-signature"
                        headingText="Employee Signature"
                        signature={employeeSignature}
                        signedAt={employeeSignedAt}
                        loading={loading}
                        onChange={onEmployeeSignatureChange}
                    />
                )}
            />
            <ShowHide
                evaluator={employeeSignOffMethodKey === emailSignOffMethodKey}
                show={(
                    <FormTextInput
                        id="sign-off-email-address"
                        headingText="Driver Email Address"
                        helperText="The sign-off link is sent to this address for this review only - the driver's profile is not changed."
                        value={signOffEmailAddress}
                        loading={loading}
                        disabled={saving}
                        dangerHelperText={signOffEmailValidationMessage}
                        onChange={onEmailAddressChange}
                    />
                )}
            />
            <ShowHide
                evaluator={signOff?.requireManagerSignature === true}
                show={(
                    <SignatureField
                        id="manager-signature"
                        headingText="Manager Signature"
                        signature={managerSignature}
                        signedAt={managerSignedAt}
                        loading={loading}
                        onChange={onManagerSignatureChange}
                    />
                )}
            />
            <FormCheckbox
                id="sign-off-confirm"
                checked={confirmChecked}
                label="I confirm this review has been completed and discussed with the employee."
                disabled={saving}
                onChange={onConfirmCheckedChange}
            />
        </div>
    );
}

SignOffStep.propTypes = {
    signOff: PropTypes.object,
    employeeSignOffMethodId: PropTypes.number,
    employeeSignature: PropTypes.string,
    employeeSignedAt: PropTypes.instanceOf(Date),
    signOffEmailAddress: PropTypes.string,
    signOffEmailValidationMessage: PropTypes.string,
    managerSignature: PropTypes.string,
    managerSignedAt: PropTypes.instanceOf(Date),
    confirmChecked: PropTypes.bool,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    onSignOffMethodChange: PropTypes.func.isRequired,
    onEmployeeSignatureChange: PropTypes.func.isRequired,
    onSignOffEmailAddressChange: PropTypes.func.isRequired,
    onManagerSignatureChange: PropTypes.func.isRequired,
    onConfirmChange: PropTypes.func.isRequired
};
